/**
 * Common RadioButton component used by other mudules as a reusable component which returns ui for radio button i for radio button and required fields can be passed as props
 *
 * @module views/components/RadioButton
 * @memberof -Common
 */

import './RadioButton.scss';

import React, { useCallback, useRef } from 'react';

import { useField } from 'formik';
import PropTypes from 'prop-types';
import { Radio as PalRadio } from 'web-palette/dist/components/Radio/Radio';

import { useOverlay } from '@ulta/core/providers/OverlayProvider/OverlayProvider';
import { processClientActionType } from '@ulta/core/utils/clientActionProcessor/clientActionProcessor';
import { constants } from '@ulta/core/utils/constants/constants';
import datacapture from '@ulta/core/utils/datacapture/datacapture';
import { spreadProps } from '@ulta/core/utils/dom/dom';
import { handleEmptyObjects } from '@ulta/core/utils/handleEmptyObjects/handleEmptyObjects';

import * as utils from './RadioButton';

/**
  * Represents a RadioButton component
  *
  * @method
  * @param {RadioButtonProps} props - React properties passed from composition
  * @returns RadioButton
  */
export const RadioButton = function( props ){
  const {
    action,
    alignment,
    crossButtonVisibility,
    onChange,
    tabIndex,
    isChecked,
    isDefaultChecked,
    isDisabled,
    ...rest
  } = props;
  const [field] = useField( props );
  const { openOverlay, isOverlay } = useOverlay();
  const radioRef = useRef();

  const handleChange = useCallback( utils.composeOnChange(
    {
      action,
      field,
      radioRef,
      alignment,
      crossButtonVisibility
    },
    { openOverlay, isOverlay, onChange }
  ), [action, alignment, crossButtonVisibility, field, isOverlay, onChange, openOverlay] );

  // Sanitize tabIndex to be either 0 or -1 to follow accessibility best practices.
  const sanitizedIndex =  typeof tabIndex === 'number' ? Math.max( -1, Math.min( 0, tabIndex ) ) : undefined;

  return (
    <div className='RadioButton'>
      <PalRadio
        inputRef={ radioRef }
        checked={ isChecked }
        defaultChecked={ isDefaultChecked }
        disabled={ isDisabled }
        name={ field.name }
        tabIndex={ sanitizedIndex }
        onChange={ handleChange }
        { ...spreadProps( { ...rest } ) }
      />
    </div>

  );
};

/**
  * Handles onClick for radio buttons
  * @param {object} data arguments
  * @param {object} data.field Formik field helper
  * @param {object} data.action DXL action
  * @param {object} data.radioRef Radio el ref
  * @param {string} data.alignment Flyout alignment
  * @param {boolean} data.crossButtonVisibility Flyout/modal crossButtonVisibility
  * @param {object} methods - An object containing helper methods.
  * @param {function} methods.isOverlay - Checks if the navigation type is an overlay.
  * @param {function} methods.openOverlay - Function to open an overlay.
  * @param {function} methods.onChange - The original onChange callback passed in props.
  */
export const composeOnChange = ( data, methods ) => ( e ) => {
  const { action, field, radioRef = {}, alignment, crossButtonVisibility } = handleEmptyObjects( data );
  const { isOverlay, openOverlay, onChange } = methods || {};
  const { navigationType, dataCaptureData, clientActionType } = action || {};

  if( !field?.onChange || !e?.target ){
    return;
  }

  field.onChange( e );
  onChange && onChange( e );

  if( !e.target.value || e.target.value === 'off' ){
    return;
  }

  if( dataCaptureData ){
    datacapture.processEvents( { dataCapture: { ...dataCaptureData } }, constants.DATACAPTURE_EVENT_TYPE.click );
  }

  if( clientActionType ){
    processClientActionType( { action } );
  }

  if( !isOverlay?.( navigationType ) ){
    return;
  }

  openOverlay( {
    type: action.navigationType,
    opener: radioRef,
    alignment,
    ...( action && { action } ),
    ...( crossButtonVisibility && { crossButtonVisibility } )
  } );
};

/**
  * Property type definitions
  * @typedef RadioButton
  * @type {object}
  * @property {string} ariaDescribedBy - Sets aria-describedBy for the input.
  * @property {string} ariaLabel - Sets aria-label for the input.
  * @property {node} children - Custom children for the Radio
  * @property {string} id - Sets id for the radio input
  * @property {boolean} isChecked - Set the radio button is checked or unchecked
  * @property {boolean} isDefaultChecked -  set the default checked value of a checkbox
  * @property {boolean} isDisabled - the button is un-clickable
  * @property {string} name - Sets name for the radio input
  * @property {string} screenReaderText - Sets screen reader text for the radio
  * @property {boolean} isFullyClickable - Sets if clicking on the custom children checks the radio
  * @property {string} title - Sets the title text of the radio
  * @property {number} count - Sets the count of the radio
  * @property {number} tabIndex - Sets the tab focus for element
  * @property {function} onChange - Sets the onChange when this function is called
  * @property {string} value - Sets the value which is required field
  * @property {object} action - Set the value for radio button action
  * @property {string} alignment - Set the alignment if there's an overlay
  * @property {boolean} crossButtonVisibility - Set the crossButtonVisibility if there's an overlay
  *
  */
export const propTypes = {
  /**
   * Sets aria-describedBy for the input.
   */
  ariaDescribedBy: PropTypes.string,
  /**
   * Sets aria-label for the input.
   */
  ariaLabel: PropTypes.string,
  /**
   * Custom children for the Radio
   */
  children: PropTypes.node,
  /**
   * Sets id for the radio input
   */
  id: PropTypes.string.isRequired,
  /**
   * Sets checked state of the radio button
   */
  isChecked: PropTypes.bool,
  /**
   * Sets defaultChecked state of the radio button
   */
  isDefaultChecked: PropTypes.bool,
  /**
   * Sets the disabled state of the radio button
   */
  isDisabled: PropTypes.bool,
  /**
   * Sets name for the radio input
   */
  name: PropTypes.string,
  /**
   * Sets screen reader text for the radio
   */
  screenReaderText: PropTypes.string,
  /**
   * Sets if clicking on the custom children checks the radio
   */
  isFullyClickable: PropTypes.bool,
  /**
   * Sets the title text of the radio
   */
  title: PropTypes.string,
  /**
   * Sets the count of the radio
   */
  count: PropTypes.number,
  /**
   * Sets the tabindex for the radio input
   */
  tabIndex: PropTypes.number,
  /**
   * Sets the onChange when this function is called
   */
  onChange: PropTypes.func,
  /**
   * Sets the value
   */
  value: PropTypes.string.isRequired,
  /**
   * Set the value for radio button action
   */
  action: PropTypes.object,
  /**
   * Set the alignment if there's an overlay
   */
  alignment: PropTypes.string,
  /**
   * Set the crossButtonVisibility if there's an overlay
   */
  crossButtonVisibility: PropTypes.bool
};

RadioButton.propTypes = propTypes;

export default RadioButton;
